/** Paste rows from the old training-enquiries spreadsheet → preview → import into the board. */
import React, { useMemo, useState } from 'react';
import { ClipboardPaste, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const SECTORS = [['probound', 'ProBound'], ['proepoxy', 'ProEpoxy'], ['prodecor', 'ProDecor']];
const SOURCES = [['google', 'Google'], ['meta', 'Meta (Facebook / Instagram)']];
const STATUSES = [['new', 'New'], ['contacted', 'Contacted'], ['hot', 'Hot'], ['booked', 'Booked'], ['lost', 'Lost'], ['archived', 'Archived']];
const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const isPhone = (v) => /^[+\d][\d\s()-]{8,}$/.test(v) && (v.match(/\d/g) || []).length >= 9;
const toIso = (v) => {
  const s = v.trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/);
  if (m) { const y = m[3].length === 2 ? `20${m[3]}` : m[3]; return `${y}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`; }
  const d = new Date(s); return Number.isNaN(d.getTime()) ? null : new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

/** Each pasted line: cells are classified by content (email / phone / date / name / notes) so column order doesn't matter. */
export function parseEnquiryRows(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const rows = [];
  for (const line of lines) {
    const cells = line.split(/\t|,(?=(?:[^"]*"[^"]*")*[^"]*$)|\s{2,}/).map((c) => c.replace(/^"|"$/g, '').trim()).filter(Boolean);
    if (!cells.length) continue;
    const row = { name: '', phone: '', email: '', lead_date: '', notes: '' };
    const rest = [];
    for (const c of cells) {
      if (!row.email && isEmail(c)) row.email = c.toLowerCase();
      else if (!row.phone && isPhone(c)) row.phone = c.replace(/\s+/g, ' ');
      else if (!row.lead_date && /\d/.test(c) && /[/.-]/.test(c) && toIso(c)) row.lead_date = toIso(c);
      else rest.push(c);
    }
    if (/^(name|first name|full name)$/i.test(rest[0] || '')) continue; // header row
    row.name = rest.shift() || '';
    if (rest.length && /^[a-z'-]+$/i.test(rest[0]) && row.name.split(' ').length === 1) row.name = `${row.name} ${rest.shift()}`; // first/last split columns
    row.notes = rest.join(' · ');
    if (row.name) rows.push(row);
  }
  return rows;
}

export function EnquiryImportModal({ onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [text, setText] = useState('');
  const [rows, setRows] = useState([]);
  const [sector, setSector] = useState('probound');
  const [source, setSource] = useState('google');
  const [status, setStatus] = useState('new');
  const [defaultDate, setDefaultDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const parsed = useMemo(() => parseEnquiryRows(text), [text]);

  const upd = (i, patch) => setRows((cur) => cur.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const submit = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/training-enquiries/import`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: rows.map((r) => ({ ...r, lead_date: r.lead_date || null, notes: r.notes || null })), interested_course: sector, hear_about: source, status, default_date: defaultDate || null }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Import failed');
      toast.success(`${d.added} enquir${d.added === 1 ? 'y' : 'ies'} added to ${STATUSES.find(([k]) => k === status)[1]}${d.skipped.length ? ` · ${d.skipped.length} skipped (already on the board)` : ''}`);
      onImported(d);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const Chips = ({ value, onChange, options, testid }) => (
    <div className="flex flex-wrap gap-1.5">{options.map(([k, l]) => <button key={k} type="button" onClick={() => onChange(k)} className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${value === k ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-200 bg-white text-gray-700'}`} data-testid={`${testid}-${k}`}>{l}</button>)}</div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="enquiry-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Upload className="w-5 h-5" /> Import enquiries from your spreadsheet</DialogTitle>
          <DialogDescription>Copy the rows from your sheet (name, phone, email, date, notes — any order) and paste them below. Pick the sector, where the leads came from, and which column they should land in.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><div className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Sector</div><Chips value={sector} onChange={setSector} options={SECTORS} testid="enq-import-sector" /></div>
          <div><div className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Lead came from</div><Chips value={source} onChange={setSource} options={SOURCES} testid="enq-import-source" /></div>
          <div><div className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Put them in</div><Chips value={status} onChange={setStatus} options={STATUSES} testid="enq-import-status" /></div>
          <div><div className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-1">Lead date (used when a row has no date)</div><Input type="date" value={defaultDate} max={todayIso()} onChange={(e) => setDefaultDate(e.target.value)} className="h-9 w-44" data-testid="enq-import-default-date" /></div>
        </div>
        {step === 1 ? (
          <>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder={'Paste rows here, e.g.\nJohn Smith\t07700 900123\tjohn@example.com\t12/09/2026\tWants ProBound course in October'} className="w-full border rounded-lg p-3 font-mono text-xs" data-testid="enq-import-text" />
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500" data-testid="enq-import-parsed-count">{parsed.length} row{parsed.length === 1 ? '' : 's'} detected</span>
              <Button onClick={() => { setRows(parsed); setStep(2); }} disabled={!parsed.length} className="bg-gray-900 text-white font-bold" data-testid="enq-import-preview"><ClipboardPaste className="w-4 h-4 mr-1" /> Preview</Button>
            </div>
          </>
        ) : (
          <>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-xs min-w-[640px]" data-testid="enq-import-table">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500"><tr><th className="text-left px-2 py-1.5">Name</th><th className="text-left px-2 py-1.5">Phone</th><th className="text-left px-2 py-1.5">Email</th><th className="text-left px-2 py-1.5">Lead date</th><th className="text-left px-2 py-1.5">Notes</th><th /></tr></thead>
                <tbody className="divide-y">
                  {rows.map((r, i) => (
                    <tr key={i} data-testid={`enq-import-row-${i}`}>
                      <td className="px-1 py-1"><input value={r.name} onChange={(e) => upd(i, { name: e.target.value })} className="w-32 h-7 border rounded px-1.5" /></td>
                      <td className="px-1 py-1"><input value={r.phone} onChange={(e) => upd(i, { phone: e.target.value })} className="w-28 h-7 border rounded px-1.5" /></td>
                      <td className="px-1 py-1"><input value={r.email} onChange={(e) => upd(i, { email: e.target.value })} className="w-40 h-7 border rounded px-1.5" /></td>
                      <td className="px-1 py-1"><input type="date" value={r.lead_date} onChange={(e) => upd(i, { lead_date: e.target.value })} className="h-7 border rounded px-1" /></td>
                      <td className="px-1 py-1"><input value={r.notes} onChange={(e) => upd(i, { notes: e.target.value })} className="w-44 h-7 border rounded px-1.5" /></td>
                      <td className="px-1 py-1"><button type="button" onClick={() => setRows((c) => c.filter((_, j) => j !== i))} className="text-rose-600 text-xs" aria-label="Remove row">✕</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="sm" onClick={() => setStep(1)} data-testid="enq-import-back">← Back to paste</Button>
              <Button onClick={submit} disabled={busy || !rows.length || rows.some((r) => !r.name.trim())} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="enq-import-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Import ${rows.length} into ${STATUSES.find(([k]) => k === status)[1]}`}</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
